import {
  Inbox,
  Store,
  Users,
  Calendar,
  Eye,
  Check,
  Trash2,
  ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { formatShortDate } from "./shared";

/**
 * Admin Dashboard landing panel — KPIs, notifications & quick actions.
 * Receives all state via props so it stays purely presentational.
 */
const DashboardPanel = ({
  user,
  counts,
  vendorsNeedingReview,
  onSetSection,
  onSetVendorSubTab,
  onMarkReviewComplete,
  reloadVendors,
  reloadCouples,
}) => {
  const wipeTestData = async () => {
    if (!window.confirm(
      "Wipe all QA/test rows from vendors & couples? This cannot be undone."
    )) return;
    try {
      const { data } = await api.post(
        "/admin/maintenance/wipe-test-data"
      );
      toast(
        `Wiped: ${data.vendors_deleted} vendors, ${data.couples_deleted} couples`,
        { duration: 3200 }
      );
      reloadVendors();
      reloadCouples();
    } catch (err) {
      toast(
        err?.response?.data?.detail || "Wipe failed",
        { duration: 2800 }
      );
    }
  };

  return (
    <div data-testid="section-dashboard">
      <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
        Dashboard
      </span>
      <h1
        data-testid="admin-heading"
        className="font-serif-display mt-3 text-3xl sm:text-4xl md:text-5xl text-[var(--ld-ink)] font-medium leading-tight"
      >
        Welcome back, {user?.name?.split(" ")[0] || "Admin"}.
      </h1>
      <p className="mt-3 text-sm text-[var(--ld-ink-muted)]">
        Approve or reject pending vendor applications and review couple sign-ups in one place.
      </p>

      {/* KPIs */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { k: "pending", label: "Pending vendors", value: counts.pending, Icon: Inbox },
          { k: "approved", label: "Approved vendors", value: counts.approved, Icon: Store },
          { k: "couples-pending", label: "Pending couples", value: counts.couplesPending, Icon: Users },
          { k: "couples-approved", label: "Approved couples", value: counts.couplesApproved, Icon: Calendar },
        ].map(({ k, label, value, Icon }) => (
          <div
            key={k}
            data-testid={`kpi-${k}`}
            className="border border-[var(--ld-border)] p-5"
          >
            <div className="flex items-center justify-between text-[var(--ld-gold-deep)]">
              <Icon size={16} strokeWidth={1.5} />
              <span className="font-accent text-[10px]">{label}</span>
            </div>
            <div className="mt-3 font-serif-display text-3xl text-[var(--ld-ink)]">
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Maintenance — wipe test/QA leftover rows */}
      <div
        data-testid="dashboard-maintenance"
        className="mt-8 flex items-start justify-between gap-4 border border-[var(--ld-border)]/60 bg-[var(--ld-ivory)]/40 px-5 py-4"
      >
        <div>
          <span className="font-accent text-[10px] tracking-[0.28em] uppercase text-[var(--ld-ink-muted)]">
            Maintenance
          </span>
          <p className="mt-1 text-[13px] text-[var(--ld-ink)]/90 leading-snug">
            Removes leftover QA / pytest rows (auth fixtures,{" "}
            <code className="font-mono text-[11px]">Pic Co *</code>,
            <code className="font-mono text-[11px]"> test-e2e-*</code>,
            etc). Real vendors &amp; couples are untouched.
          </p>
        </div>
        <button
          type="button"
          data-testid="wipe-test-data-btn"
          onClick={wipeTestData}
          className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 text-[11px] tracking-[0.18em] uppercase border border-rose-500/40 text-rose-600 hover:bg-rose-500 hover:text-white transition-colors"
        >
          <Trash2 size={13} strokeWidth={1.6} />
          Wipe test data
        </button>
      </div>

      {/* Notifications */}
      {vendorsNeedingReview.length > 0 && (
        <div
          data-testid="dashboard-notifications"
          className="mt-10 border border-red-500/40 bg-red-500/5"
        >
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-red-500/20">
            <div className="flex items-center gap-2">
              <span className="relative inline-flex items-center justify-center h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
              </span>
              <span className="font-accent text-[10px] tracking-[0.3em] uppercase text-red-700">
                Action needed
              </span>
            </div>
            <span
              data-testid="dashboard-notif-count"
              className="text-[11px] tracking-[0.2em] uppercase text-red-700"
            >
              {vendorsNeedingReview.length} vendor
              {vendorsNeedingReview.length === 1 ? "" : "s"} need
              {vendorsNeedingReview.length === 1 ? "s" : ""} review
            </span>
          </div>
          <ul className="divide-y divide-red-500/15">
            {vendorsNeedingReview.slice(0, 6).map((v) => (
              <li
                key={v.id}
                data-testid={`dashboard-notif-${v.id}`}
                className="flex items-center justify-between gap-3 px-5 py-3"
              >
                <div className="min-w-0">
                  <div className="font-serif-display text-[16px] text-[var(--ld-ink)] truncate">
                    {v.company_name}
                  </div>
                  <div className="text-[11px] text-[var(--ld-ink-muted)]">
                    {v.last_reviewed_at
                      ? `Last reviewed ${formatShortDate(v.last_reviewed_at)}`
                      : v.accepted_at
                      ? `Accepted ${formatShortDate(v.accepted_at)}`
                      : "Awaiting first review"}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    data-testid={`dashboard-notif-go-${v.id}`}
                    onClick={() => {
                      onSetVendorSubTab("approved");
                      onSetSection("vendor-form-data");
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border border-[var(--ld-ink)]/30 text-[var(--ld-ink)] hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors"
                  >
                    <Eye size={12} strokeWidth={1.6} />
                    Review
                  </button>
                  <button
                    type="button"
                    data-testid={`dashboard-notif-done-${v.id}`}
                    onClick={() => onMarkReviewComplete(v)}
                    title="Mark review complete — resets the 10-month timer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 transition-colors"
                  >
                    <Check size={12} strokeWidth={1.8} />
                    Done
                  </button>
                </div>
              </li>
            ))}
          </ul>
          {vendorsNeedingReview.length > 6 && (
            <div className="px-5 py-3 text-[11px] text-[var(--ld-ink-muted)] border-t border-red-500/15">
              +{vendorsNeedingReview.length - 6} more in Vendor Form data → Active.
            </div>
          )}
        </div>
      )}

      {/* Quick actions */}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          onClick={() => onSetSection("vendor-form-data")}
          data-testid="quick-vendors"
          className="text-left border border-[var(--ld-border)] p-6 hover:border-[var(--ld-ink)] transition-colors"
        >
          <ClipboardList size={18} className="text-[var(--ld-gold-deep)]" />
          <p className="mt-3 font-serif-display text-xl text-[var(--ld-ink)]">
            Review Vendor Applications
          </p>
          <p className="mt-1 text-[13px] text-[var(--ld-ink-muted)]">
            {counts.vendorsTotal} total · {counts.pending} pending
          </p>
        </button>
        <button
          onClick={() => onSetSection("couples-form-data")}
          data-testid="quick-couples"
          className="text-left border border-[var(--ld-border)] p-6 hover:border-[var(--ld-ink)] transition-colors"
        >
          <Users size={18} className="text-[var(--ld-gold-deep)]" />
          <p className="mt-3 font-serif-display text-xl text-[var(--ld-ink)]">
            Review Couple Sign-ups
          </p>
          <p className="mt-1 text-[13px] text-[var(--ld-ink-muted)]">
            {counts.couplesTotal} total · {counts.couplesNew} new
          </p>
        </button>
      </div>
    </div>
  );
};

export default DashboardPanel;
